import axios from 'axios'
import {AuthModel} from './_models'
import {
  ADMIN_AUTH_KEY,
  AUTH_LOCAL_STORAGE_KEY,
  BUYER_INFO_KEY,
  getAuth,
  setAuth,
} from './AuthHelpers'

const API_URL = process.env.REACT_APP_KT_API_URL

// Admin "Buyer View": the admin's own auth is parked in ADMIN_AUTH_KEY (AuthHelpers) while the
// buyer-view token (read-only, acts as the buyer) sits in the normal auth key.
export const BUYER_VIEW_PERMISSION = 'can-view-as-buyer'
export type BuyerOption = {id: number; name: string; email?: string; codes: string[]}

const read = <T>(key: string): T | undefined => {
  try {
    const value = localStorage.getItem(key)
    return value ? (JSON.parse(value) as T) : undefined
  } catch {
    return undefined
  }
}

export const getBuyerViewInfo = (): {id: number; name: string} | undefined =>
  read(BUYER_INFO_KEY)

export const isBuyerView = (): boolean => !!getBuyerViewInfo() && !!read(ADMIN_AUTH_KEY)

// Active buyers (with an email) for the picker. Always called with the admin token.
export async function fetchBuyerOptions(): Promise<BuyerOption[]> {
  const adminAuth = read<AuthModel>(ADMIN_AUTH_KEY) ?? getAuth()
  const res = await axios.get(`${API_URL}/users/buyer-view-list`, {
    headers: {Authorization: `Bearer ${adminAuth?.access_token}`},
  })
  const rows: any[] = res.data?.data ?? []
  return rows.map((row) => ({
    id: row.id,
    name: row.name || row.email,
    email: row.email,
    codes: (row.buyer_codes ?? []).map((code: any) => code.code),
  }))
}

// Starts (or switches) buyer view, then reloads so every screen and cache starts clean.
export async function startBuyerView(buyer: {id: number; name: string}) {
  const adminAuth = read<AuthModel>(ADMIN_AUTH_KEY) ?? getAuth()
  const res = await axios.post(`${API_URL}/auth/buyer-view/${buyer.id}`, undefined, {
    headers: {Authorization: `Bearer ${adminAuth?.access_token}`},
  })
  const token: string | undefined = res.data?.data?.access_token ?? res.data?.access_token
  if (!token || !adminAuth) throw new Error('Could not start buyer view')

  localStorage.setItem(ADMIN_AUTH_KEY, JSON.stringify(adminAuth))
  localStorage.setItem(BUYER_INFO_KEY, JSON.stringify({id: buyer.id, name: buyer.name}))
  setAuth({access_token: token})
  window.location.href = '/dashboard'
}

// Restores the admin's own token. Never requires a new login.
export async function exitBuyerView() {
  const adminAuth = read<AuthModel>(ADMIN_AUTH_KEY)
  const buyerAuth = getAuth()
  try {
    // Best-effort audit entry; ignore failures so the admin can always get back
    await axios.post(`${API_URL}/auth/buyer-view-exit`, undefined, {
      headers: {Authorization: `Bearer ${buyerAuth?.access_token}`},
    })
  } catch {}
  if (adminAuth) {
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, JSON.stringify(adminAuth))
  }
  localStorage.removeItem(ADMIN_AUTH_KEY)
  localStorage.removeItem(BUYER_INFO_KEY)
  window.location.href = '/dashboard'
}
